// node_modules
import { FC, useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
// Components
import { TechnologyRequirementsTrlLevel } from ".";
import { AsynchronousWrapper, FindestTextBox, FindestButton } from "Components";
import { EditableTechnologyRequirementScore } from "./EditableTechnologyRequirementScore";
// Custom hooks
import { useFetch } from "Hooks";
// Contexts
import { CaseContext, AuthContext } from "Stores";
// Types
import { TRequirementDTO, TTechnologyCaseRequirementsDTO, TTechnologyRequirementsDTO, TUseFetch } from "Types";
// styles
import styles from "./technologyRequirements.module.scss";
// Controllers
import { RequirementControllerSingleton } from "Controllers";
// Enums
import { ToastTypeEnum } from "Enums";
// Helpers
import { PendoHelperSingleton, ToastHelperSingleton } from "Helpers";
import { LoggingConstants } from "Constants";

export const TechnologyRequirements: FC = () => {
    // Shared states
    const { caseState } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);

    // State
    const [technologyRequirements, setTechnologyRequirements] = useState<TTechnologyRequirementsDTO>();

    const [isCreatingRequirement, setIsCreatingRequirement] = useState(false);
    const [newRequirementName, setNewRequirementName] = useState<string | undefined>(undefined);

    // We use `useFetch` custom hook here
    // to fetch data from the server
    const { isLoading, didError, errorMessage, fetchedData }: TUseFetch<TTechnologyRequirementsDTO> = useFetch(`api/technologies/${caseState.selectedTechnologyId}/requirements`);

    // Logic
    useEffect(() => {
        if (!fetchedData) return;
        setTechnologyRequirements(fetchedData);
    }, [fetchedData]);

    // Logging
    useEffect(() => {
        // Log the fetching of the requirements
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyGetRequirements`, {
            technologyId: caseState.selectedTechnologyId
        });
    }, [caseState.selectedTechnologyId]);

    const onChangeRequirementName = (text: string) => {
        setNewRequirementName(text);
    };

    const saveRequirementColumn = async () => {
        if (!caseState.selectedCaseId) return;

        if (!newRequirementName || newRequirementName.trim().length === 0) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Please enter a valid requirement name.");
            return;
        }
        setIsCreatingRequirement(false);

        const requirement: TRequirementDTO = {
            id: -1,
            caseId: caseState.selectedCaseId, // CaseId has to be something to show the requiremenstable
            name: newRequirementName,
            type: 1,
            isUserAdded: true
        };
        const newRequirement = await RequirementControllerSingleton.addRequirement(caseState.selectedCaseId, requirement, true);
        if (technologyRequirements) {
            technologyRequirements.technologyRequirements = technologyRequirements.technologyRequirements.concat({ id: newRequirement.id, name: newRequirement.name, scores: [] } as TTechnologyCaseRequirementsDTO);
            setTechnologyRequirements(technologyRequirements);
        }
        setNewRequirementName(undefined);
    };

    function handleSetIsCreatingRequirement() {
        setIsCreatingRequirement(true);
    }

    return (
        <AsynchronousWrapper didError={didError} errorMessage={errorMessage} isLoading={isLoading} LoaderComponent={null}>
            {fetchedData ?
                <div className={styles.technologyRequirementsContainer}>
                    <div className={styles.technologyRequirement} key="trl">
                        <div className={styles.requirementTitle}>Maturity level</div>
                        <div className={styles.requirementValue}><TechnologyRequirementsTrlLevel technologyId={caseState.selectedTechnologyId!} 
                            isEditable={authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery} technologyReadinessLevel={fetchedData.technologyReadinessLevel}
                            resourceName={`requirementTrl${caseState.selectedOriginalTechnologyId ? caseState.selectedOriginalTechnologyId : caseState.selectedTechnologyId}`}/>
                        </div>
                    </div>

                    {technologyRequirements && technologyRequirements.technologyRequirements.length >= 1 && technologyRequirements?.technologyRequirements.map((requirement) => {
                        let currentScore = requirement.scores[0];
                        for (let i = 0; i < requirement.scores.length; i++) {
                            const requirementScore = requirement.scores[i];
                            if (requirementScore.isUserAdded && requirementScore.score && requirementScore.score.trim().length > 0) {
                                currentScore = requirementScore;
                                break;
                            }
                        }
                        if (caseState.selectedTechnologyId) {
                            return (
                                <EditableTechnologyRequirementScore
                                    key={requirement.id}
                                    showRequirementName={true}
                                    requirement={requirement}
                                    score={currentScore}
                                    technologyId={caseState.selectedTechnologyId}
                                    originalTechnologyId={caseState.selectedOriginalTechnologyId ? caseState.selectedOriginalTechnologyId : caseState.selectedTechnologyId} />
                            );
                        } else {
                            return null;
                        }
                    })}
                    {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ?
                        <>
                            {isCreatingRequirement ?
                                <div className={styles.addNewRowInput}>
                                    <FindestTextBox doAutoFocus={true} onChange={onChangeRequirementName} width={200} placeholder={"Requirement"} />
                                    <div className={styles.addNewRowSave} title={"Create new row"} onClick={saveRequirementColumn}><FontAwesomeIcon icon={faSave} /></div>
                                </div> : null}
                            <FindestButton title={"+ Add new requirement row"} onClick={handleSetIsCreatingRequirement} extraClassName={styles.addNewRowButton} />
                        </>
                        : null}
                </div>
                :
                <></>
            }
        </AsynchronousWrapper>
    );
};